import { useEffect, useMemo, useState } from 'react';

/** Page sizes offered under every table. The smallest also decides when paging is worth showing. */
export const PAGE_SIZES = [10, 25, 50];
export const DEFAULT_PAGE_SIZE = 25;

/** Zero-based index of the last page that has rows; 0 for an empty list. */
export function lastPageIndex(totalItems: number, pageSize: number): number {
  return Math.max(0, Math.ceil(totalItems / pageSize) - 1);
}

/**
 * Paging for lists the API returns whole. Pass the list after filtering, so the page count and
 * "of N items" describe what the user can actually see.
 */
export function useClientPagination<T>(items: T[]) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const lastPage = lastPageIndex(items.length, pageSize);
  // A delete or a narrower filter can shrink the list under the current page. Render the last page
  // that exists straight away, and move the stored page there so it does not jump back later.
  const current = Math.min(page, lastPage);
  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  const pageItems = useMemo(
    () => items.slice(current * pageSize, (current + 1) * pageSize),
    [items, current, pageSize],
  );

  return {
    page: current,
    pageSize,
    pageItems,
    totalItems: items.length,
    /** Back to the first page, for when the filter that produces `items` changes. */
    reset: () => setPage(0),
    onChange: (nextPage: number, nextPageSize: number) => {
      setPage(nextPage);
      setPageSize(nextPageSize);
    },
  };
}
